import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { client } from "../../utils/axiosClient";

const queryConfig = {
  enabled: true,
  refetchInterval: 1800000,
  staleTime: Infinity,
  placeholderData: (prev: any) => prev, // replaces keepPreviousData
};

export const useGetTodos = (userId: number) => {
  return useQuery({
    queryKey: ["getTodos", userId],
    queryFn: async () => {
      const { data } = await client.get(`/dashboard/get-todos?userId=${userId}`);
      return data;
    },
    ...queryConfig,
  });
};

export const useCreateTodo = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, title }: { userId: number; title: string }) => {
      const { data } = await client.post(`/dashboard/create-todo`, { userId, title });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getTodos"] });
    },
  });
};

export const useUpdateTodo = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      userId, todoId, status, title, backgroundColor,
    }: {
      userId: number; todoId: number; status: number; title: string; backgroundColor?: string;
    }) => {
      const { data } = await client.put(`/dashboard/update-todo`, { userId, todoId, status, title, backgroundColor });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getTodos"] });
    },
  });
};

export const useDeleteTodo = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, todoId }: { userId: number; todoId?: number }) => {
      const { data } = await client.delete(`/dashboard/delete-todo`, { params: { userId, todoId } });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getTodos"] });
    },
  });
};

export const useDeleteAllTodo = (userId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await client.delete(`/dashboard/delete-all-todo`, { params: { userId } });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getTodos"] });
    },
  });
};

export const useGetOngoingProjectCount = (userId: number) => {
  return useQuery({
    queryKey: ["getOngoingProjects", userId],
    queryFn: async () => {
      const { data } = await client.get(`/dashboard/project-count?userId=${userId}`);
      return data[0];
    },
    ...queryConfig,
  });
};

export const useGetAllTourStatus = (userId: number) => {
  return useQuery({
    queryKey: ["getAllTourStatus", userId],
    queryFn: async () => {
      const { data } = await client.get(`/dashboard/tour-status?userId=${userId}`);
      return data;
    },
    ...queryConfig,
  });
};

export const useUpdateTourStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      userId, tourStatus,
    }: {
      userId: number;
      tourStatus: "dashboardTourStatus" | "scheduleTourStatus" | "libraryTourStatus" | "messagesTourStatus";
    }) => {
      const { data } = await client.put(`/dashboard/update-tour-status`, { userId, tourStatus });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getAllTourStatus"] });
    },
  });
};

export const useGetCompletedProjectCount = (userId: number) => {
  return useQuery({
    queryKey: ["getCompletedProjectCount", userId],
    queryFn: async () => {
      const { data } = await client.get(`/dashboard/completed-project-count?userId=${userId}`);
      return data[0];
    },
    ...queryConfig,
  });
};

export const useGetMyTasks = ({
  userId, status, projectId,
}: {
  userId: number; status: number; projectId: number | string;
}) => {
  return useQuery({
    queryKey: ["getMyTasks", userId, status, projectId],
    queryFn: async () => {
      const { data } = await client.get(
        `/dashboard/my-tasks?userId=${userId}&status=${status}&projectId=${projectId}`
      );
      return data;
    },
    ...queryConfig,
  });
};

export const useGetWorkspaces = () => {
  return useQuery({
    queryKey: ["getWorkspaces"],
    queryFn: async () => {
      const { data } = await client.get("/auth/get-workspaces");
      return data;
    },
    ...queryConfig,
  });
};

export const useGetWorkspacesById = (id: string) => {
  return useQuery({
    queryKey: ["getWorkspacesById", id],
    queryFn: async () => {
      const { data } = await client.get(`/auth/get-workspaces-by-id`, { params: { id } });
      return data;
    },
    ...queryConfig,
  });
};

export const useChangeWorkspace = () => {
  return useMutation({
    mutationFn: async ({ tenantId }: { tenantId: string }) => {
      const { data } = await client.post("/auth/change-workspace", { tenantId });
      const user = { ...JSON.parse(localStorage.getItem("user")), token: data.token };
      localStorage.setItem("user", JSON.stringify(user));
      return data;
    },
  });
};